import { useCallback, useState } from "react";
import { Colors } from "../../../../utils/colors";
import HelpModal from "../../../Containers/HelpModal";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import SettingsOption from "../../../EventDashboard/SettingsOption";
import {
  DMAutomation,
  DMAutomationState,
  NotificationType,
} from "@markit/common.types";
import { DMAutomationConfirmDeleteModal } from "../Items/DMAutomationConfirmDeleteModal";
import CloneDMAutomationModal from "../Items/CloneDMAutomationModal";
import { toggleDMAutomationState } from "../../../../utils/dmAutomationUtils";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { useDispatch, useSelector } from "react-redux";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import { NavigationId } from "../../../../navigation/AppParamList";
import { useNavigate } from "react-router-dom";

type DMAutomationSettingsPanelProps = {
  dmAutomation: DMAutomation;
};

const DMAutomationSettingsPanel = (props: DMAutomationSettingsPanelProps) => {
  const { dmAutomation } = props;
  const { accountData } = useSelector(getAccountState).account;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);

  const styles = {
    container: { width: 400 },

    contentHeader: {
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 14,
    },

    optionsContainer: {
      backgroundColor: Colors.WHITE,
      width: "100%",
    },
  };

  const changeDMAutomationState = useCallback(async () => {
    setProcessing(true);
    await toggleDMAutomationState(
      accountData.uid,
      dmAutomation.id,
      dmAutomation.state === DMAutomationState.ACTIVE
        ? DMAutomationState.INACTIVE
        : DMAutomationState.ACTIVE
    );
    showNotificationBanner(
      dispatch,
      dmAutomation.state === DMAutomationState.ACTIVE
        ? "Paused Automation"
        : "Reactivated Automation",
      NotificationType.AFFIRMATIVE
    );
    setProcessing(false);
  }, [accountData.uid, dispatch, dmAutomation.id, dmAutomation.state]);

  return (
    <div>
      <div className={"RowNormal"} style={{ gap: 30 }}>
        <div style={styles.container}>
          <div style={styles.contentHeader}>Data</div>
          <StandardBorderedContainer containerStyles={styles.optionsContainer}>
            <SettingsOption
              title={"Help"}
              subtext={""}
              iconName={"ion:help"}
              onPress={() => setShowHelpModal(true)}
              disabled={false}
            />
          </StandardBorderedContainer>
        </div>
        <div style={styles.container}>
          <div style={styles.contentHeader}>More</div>
          <StandardBorderedContainer containerStyles={styles.optionsContainer}>
            {dmAutomation.state !== DMAutomationState.DRAFT ? (
              <div>
                <SettingsOption
                  title={
                    dmAutomation.state === DMAutomationState.ACTIVE
                      ? "Pause Automation"
                      : "Reactivate Automation"
                  }
                  subtext={`${
                    dmAutomation.state === DMAutomationState.ACTIVE
                      ? "Users will no longer enter this flow until reactivated"
                      : "Users will be able to enter this flow again"
                  }`}
                  iconName={
                    dmAutomation.state === DMAutomationState.ACTIVE
                      ? "ion:pause-circle"
                      : "ion:play-circle"
                  }
                  onPress={changeDMAutomationState}
                  disabled={false}
                  loading={processing}
                />
                <HorizontalDivider />
              </div>
            ) : null}
            <div>
              <SettingsOption
                title={"Clone DM Automation"}
                subtext={"Creates a new DM automation with the same content"}
                iconName={"ion:copy"}
                onPress={() => setShowCloneModal(true)}
                disabled={false}
              />
              <HorizontalDivider />
            </div>
            <div>
              <SettingsOption
                title={"Delete Automation"}
                subtext={"This can't be undone"}
                iconName={"ion:trash"}
                onPress={() => setShowDeleteModal(true)}
                disabled={false}
                altColor={Colors.RED1}
              />
            </div>
          </StandardBorderedContainer>
        </div>
      </div>
      <DMAutomationConfirmDeleteModal
        id={dmAutomation.id}
        dmAutomationId={dmAutomation.id}
        type="Automation"
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        handleAfterDelete={() => navigate(NavigationId.HOME_AUTO_DM)}
      />
      <HelpModal showModal={showHelpModal} setShowModal={setShowHelpModal} />
      {showCloneModal ? (
        <CloneDMAutomationModal
          dmAutomation={dmAutomation}
          isModalVisible={showCloneModal}
          setIsModalVisible={setShowCloneModal}
        />
      ) : null}
    </div>
  );
};

export default DMAutomationSettingsPanel;
